import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, ButtonLink } from '@clay-run/glaze/button';
import { Heading } from '@clay-run/glaze/heading';
import { Text } from '@clay-run/glaze/text';
import { useAppBasePath } from '../../_shared/base-path';
import { useMetaAdPreview, useMetaCampaign } from '../api-client';
import { costPer, count, metaResults, pct, usd } from '../format';
import { MetaCtaTypeSchema, type MetaAd, type MetaCampaignDetail } from '../types';
import { MetaFeedPreview } from './AdPreviews';
import { CampaignAsk } from './CampaignAsk';
import { MetaStatusBadge } from './CampaignStatusBadge';
import { CplChart } from './CplChart';
import { Kpi, Panel } from './Panels';

const PREVIEW_IFRAME_SRC = /<iframe[^>]+src="(https:\/\/[^"]+\.facebook\.com\/[^"]+)"/;

function MetaRenderedPreview({ adId }: { adId: string }) {
  const preview = useMetaAdPreview(adId);
  if (preview.isLoading) return <Text size="sm" variant="tertiary">Loading Meta's preview…</Text>;
  const src = preview.data ? PREVIEW_IFRAME_SRC.exec(preview.data)?.[1]?.replace(/&amp;/g, '&') : undefined;
  if (!src) return <Text size="sm" variant="tertiary">Meta didn't return a preview for this ad.</Text>;
  return <iframe src={src} title="Meta ad preview" className="h-[560px] w-[320px] rounded-xl border border-border-primary" />;
}

function AdCard({ ad }: { ad: MetaAd }) {
  const [showMetaPreview, setShowMetaPreview] = useState(false);
  const cta = MetaCtaTypeSchema.safeParse(ad.creative?.callToAction);

  return (
    <div className="flex w-80 flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{ad.name}</span>
        <MetaStatusBadge effectiveStatus={ad.effectiveStatus} />
      </div>
      {showMetaPreview ? (
        <MetaRenderedPreview adId={ad.id} />
      ) : (
        <MetaFeedPreview
          label="Creative"
          content={{
            primaryText: ad.creative?.body ?? '',
            headline: ad.creative?.title ?? '',
            description: '',
            callToAction: cta.success ? cta.data : null,
            destinationUrl: ad.creative?.linkUrl ?? '',
            imageUrl: ad.creative?.imageUrl ?? null,
          }}
        />
      )}
      <Button variant="ghost" size="sm" onClick={() => setShowMetaPreview(v => !v)}>
        {showMetaPreview ? 'Show creative fields' : "Show Meta's rendered preview"}
      </Button>
    </div>
  );
}

function MetaCampaignDetailView({ detail }: { detail: MetaCampaignDetail }) {
  const base = useAppBasePath();
  const { campaign: c, daily, ads } = detail;
  const results = metaResults(c.metrics);
  const dailyCost = daily.map(d => {
    const r = metaResults(d).value;
    return r > 0 ? Math.round((d.spendUsd / r) * 100) / 100 : 0;
  });

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <Link to={`${base}/performance`} className="text-xs text-content-tertiary hover:underline">
            Performance
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <Heading level={1} size="lg">
              {c.name}
            </Heading>
            <MetaStatusBadge effectiveStatus={c.effectiveStatus} />
            <Text size="sm" variant="tertiary">
              Meta · {c.objective.replace('OUTCOME_', '').toLowerCase()} · {detail.adSetCount} ad set
              {detail.adSetCount === 1 ? '' : 's'}
            </Text>
          </div>
        </div>
        <ButtonLink
          variant="secondary"
          href={detail.adsManagerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto"
        >
          Open in Ads Manager
        </ButtonLink>
      </header>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi
              label="Spend"
              value={usd(c.metrics.spendUsd, 2)}
              sub={c.dailyBudgetUsd === null ? 'No campaign budget' : `${usd(c.dailyBudgetUsd)}/day budget`}
            />
            <Kpi label="Impressions" value={count(c.metrics.impressions)} sub="Lifetime" />
            <Kpi
              label="Clicks"
              value={count(c.metrics.clicks)}
              sub={c.metrics.impressions > 0 ? `${pct(c.metrics.clicks / c.metrics.impressions)} CTR` : 'No impressions yet'}
            />
            <Kpi label="Results" value={count(results.value)} sub={results.label} />
            <Kpi label="Cost per result" value={costPer(c.metrics.spendUsd, results.value)} sub="Lifetime" />
          </div>

          <Panel className="flex flex-col gap-3">
            <Heading level={2} size="sm">
              Daily cost per result
            </Heading>
            {daily.length > 0 ? (
              <CplChart platform="Meta" dailyCpl={dailyCost} labels={daily.map(d => d.date.slice(5))} />
            ) : (
              <Text size="sm" variant="tertiary">
                No delivery yet. Numbers appear here once the campaign is active and spending.
              </Text>
            )}
          </Panel>

          <div className="flex flex-col gap-3">
            <Heading level={2} size="sm">
              Ads
            </Heading>
            {ads.length === 0 ? (
              <Text size="sm" variant="tertiary">
                This campaign has no ads yet.
              </Text>
            ) : (
              <div className="flex flex-wrap gap-6">
                {ads.map(ad => (
                  <AdCard key={ad.id} ad={ad} />
                ))}
              </div>
            )}
          </div>
        </div>
        <aside>
          <CampaignAsk platform="meta" campaignId={c.id} />
        </aside>
      </div>
    </section>
  );
}

export default function MetaCampaignDetailPage() {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const base = useAppBasePath();
  const detail = useMetaCampaign(campaignId);

  if (detail.isLoading) return <Text variant="tertiary">Loading campaign…</Text>;
  if (!detail.data) {
    return (
      <div className="flex flex-col gap-2">
        <Text variant="tertiary">This campaign couldn't be loaded from Meta.</Text>
        <Link to={`${base}/performance`} className="text-sm hover:underline">
          Back to performance
        </Link>
      </div>
    );
  }
  return <MetaCampaignDetailView key={campaignId} detail={detail.data} />;
}
